import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import SwyperLogo from '../components/SwypeRLogo';
import ImageUpload from '../components/ImageUpload';
import { Check, X, Loader2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const email = location.state?.email;
  const verified = location.state?.verified;

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const usernameCheckTimeout = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    username: '',
    bio: '',
    password: '',
    confirmPassword: '',
    photos: []
  });

  const [birthDay, setBirthDay] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthYear, setBirthYear] = useState('');

  const [usernameStatus, setUsernameStatus] = useState({
    checking: false,
    available: null,
    message: ''
  });

  // Redirect if not verified
  React.useEffect(() => {
    if (!email || !verified) {
      navigate('/signup');
    }
  }, [email, verified, navigate]);

  const checkUsernameAvailability = async (username) => {
    if (!username || username.length === 0) {
      setUsernameStatus({ checking: false, available: null, message: '' });
      return;
    }

    try {
      setUsernameStatus({ checking: true, available: null, message: '' });
      const response = await axios.get(`${API_URL}/api/auth/check-username`, {
        params: { username }
      });
      setUsernameStatus({
        checking: false,
        available: response.data.available,
        message: response.data.message
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to check username:', error);
      }
      setUsernameStatus({ checking: false, available: null, message: '' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Debounced username check
    if (name === 'username') {
      if (usernameCheckTimeout.current) {
        clearTimeout(usernameCheckTimeout.current);
      }

      if (value.length > 0) {
        usernameCheckTimeout.current = setTimeout(() => {
          checkUsernameAvailability(value);
        }, 500);
      } else {
        setUsernameStatus({ checking: false, available: null, message: '' });
      }
    }
  };

  const handlePhotosChange = (newPhotos) => {
    setFormData(prev => ({ ...prev, photos: newPhotos }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      toast({
        title: "Missing Information",
        description: "Please enter your name",
        variant: "destructive"
      });
      return;
    }

    if (trimmedName.length < 2) {
      toast({
        title: "Name Too Short",
        description: "Name must be at least 2 characters",
        variant: "destructive"
      });
      return;
    }

    if (trimmedName.length > 20) {
      toast({
        title: "Name Too Long",
        description: "Name must be 20 characters or less",
        variant: "destructive"
      });
      return;
    }

    if (!formData.username || !usernameStatus.available) {
      toast({
        title: "Username Required",
        description: "Please choose an available username",
        variant: "destructive"
      });
      return;
    }

    if (formData.username.length > 15) {
      toast({
        title: "Username Too Long",
        description: "Username must be 15 characters or less",
        variant: "destructive"
      });
      return;
    }

    if (!formData.password || formData.password.length < 6) {
      toast({
        title: "Password Required",
        description: "Password must be at least 6 characters",
        variant: "destructive"
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Passwords Don't Match",
        description: "Please make sure both passwords are the same",
        variant: "destructive"
      });
      return;
    }

    if (!birthDay || !birthMonth || !birthYear) {
      toast({
        title: "Date of Birth Required",
        description: "Please enter your complete date of birth",
        variant: "destructive"
      });
      return;
    }

    if (formData.photos.length === 0) {
      toast({
        title: "Photo Required",
        description: "Please upload a profile photo",
        variant: "destructive"
      });
      return;
    }

    // Calculate age
    const dob = new Date(parseInt(birthYear), parseInt(birthMonth) - 1, parseInt(birthDay));
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }

    if (age < 18) {
      toast({
        title: "Age Requirement",
        description: "You must be at least 18 years old to use Swyper",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      // Create account
      const signupData = {
        name: formData.name.trim(),
        username: formData.username,
        email: email,
        password: formData.password,
        date_of_birth: dob.toISOString().split('T')[0],
        age: age,
        location: "",  // Empty location initially - will be updated when user enables geolocation
        photos: formData.photos,
        bio: formData.bio || '',
        location_visible: true  // Default to visible
      };

      const response = await axios.post(`${API_URL}/api/auth/signup`, signupData, {
        withCredentials: true, // Enable cookies
        timeout: 10000 // 10 second timeout
      });

      if (response.data.token && response.data.user) {
        // Cookie is set automatically by the backend
        
        // Update auth context with user data
        setUser(response.data.user);

        toast({
          title: "Welcome to Swyper! 💜",
          description: "Your profile has been created successfully"
        });

        // Wait a moment for state to propagate
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Navigate to swipe page
        navigate('/swipe', { replace: true });
      } else {
        throw new Error('Account created but login failed. Please try logging in.');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Profile creation error:', error);
        console.error('Error response:', error.response);
        console.error('Error message:', error.message);
        console.error('Error config:', error.config);
      }
      
      let errorMessage = "Failed to create profile. Please try again.";
      
      if (error.code === 'ECONNABORTED') {
        errorMessage = "Request timeout. Please check your connection and try again.";
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - 18 - i);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center pt-[4px] px-4 pb-[4px]">
      <div className="w-full max-w-2xl">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex flex-col items-center mb-6">
            <div className="-mt-3">
              <SwyperLogo width={150} height={140} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mt-2">Create Your Profile</h2>
            <p className="text-sm text-gray-600 text-center mt-1">
              {email}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <Label htmlFor="name">Name (2-20 characters)</Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 20) {
                    handleChange(e);
                  }
                }}
                maxLength={20}
                placeholder="Your name here"
                required
                className="mt-1"
              />
              <div className="flex justify-between mt-1">
                <p className="text-xs text-gray-500">
                  Use your first name, nickname, or whatever you prefer
                </p>
                <span className="text-xs text-gray-500">
                  {20 - formData.name.length} left
                </span>
              </div>
            </div>

            {/* Date of Birth */}
            <div>
              <Label>Date of Birth (must be 18+)</Label>
              <div className="grid grid-cols-3 gap-3 mt-1">
                <select
                  value={birthMonth}
                  onChange={(e) => setBirthMonth(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  <option value="">Month</option>
                  {months.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <select
                  value={birthDay}
                  onChange={(e) => setBirthDay(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  <option value="">Day</option>
                  {days.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <select
                  value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  <option value="">Year</option>
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Username */}
            <div>
              <Label htmlFor="username">Username (max 15 characters)</Label>
              <div className="relative mt-1">
                <Input
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleChange}
                  maxLength={15}
                  required
                  className="pr-10"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {usernameStatus.checking && (
                    <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                  )}
                  {!usernameStatus.checking && usernameStatus.available === true && (
                    <Check className="w-5 h-5 text-green-500" />
                  )}
                  {!usernameStatus.checking && usernameStatus.available === false && (
                    <X className="w-5 h-5 text-red-500" />
                  )}
                </div>
              </div>
              {usernameStatus.message && (
                <p className={`text-sm mt-1 ${usernameStatus.available ? 'text-green-600' : 'text-red-600'}`}>
                  {usernameStatus.message}
                </p>
              )}
              <p className="text-xs text-amber-600 mt-1 font-medium">
                ⚠️ Choose carefully - your username cannot be changed later
              </p>
            </div>

            {/* Bio */}
            <div>
              <Label htmlFor="bio">Bio (optional, max 39 characters)</Label>
              <div className="mt-1">
                <textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    if (newValue.length <= 39) {
                      handleChange(e);
                    }
                  }}
                  maxLength={39}
                  rows={2}
                  placeholder="Tell us a bit about yourself..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  style={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}
                />
                <div className="flex justify-end mt-1">
                  <span className="text-sm text-gray-500">
                    {39 - (formData.bio?.length || 0)} characters remaining
                  </span>
                </div>
              </div>
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password">Password (min 6 characters)</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative mt-1">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {/* Password match indicator */}
              {formData.password && formData.confirmPassword && (
                <div className="mt-2 flex items-center gap-2">
                  {formData.password === formData.confirmPassword ? (
                    <>
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm text-green-600 font-medium">Passwords match</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className="text-sm text-red-600 font-medium">Passwords don't match</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Profile Photo */}
            <div>
              <Label>Profile Photo</Label>
              <div className="mt-2">
                <ImageUpload
                  photos={formData.photos}
                  onChange={handlePhotosChange}
                  maxPhotos={1}
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              disabled={isLoading || !usernameStatus.available}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating your profile...
                </div>
              ) : (
                'Complete Profile'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateProfile;
